import React, { useEffect, useMemo, useState } from "react";

import { Activity, HeartPulse, Loader2, Save } from "lucide-react";

import Button from "../../../Components/Button";
import CommonInput from "../../../Components/Input";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  clearHealthScoreThresholdsUpdateState,
  fetchHealthScoreThresholds,
  updateHealthScoreThresholds,
} from "../../../Store/Reducers/VendorAdminSlice";
import {
  DEFAULT_HEALTH_SCORE_THRESHOLDS,
  formatAccountHealthBandLegend,
  formatBusinessHealthBandLegend,
  normalizeHealthScoreThresholds,
  validateHealthScoreThresholds,
  type AccountHealthThresholds,
  type BusinessHealthThresholds,
  type HealthScoreThresholds,
} from "../../../Utils/HealthThresholds";
import { toast } from "../../../Utils/Helper/toast";

type ThresholdFieldProps = {
  label: string;
  hint: string;
  value: number;
  error?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
};

const ThresholdField: React.FC<ThresholdFieldProps> = ({
  label,
  hint,
  value,
  error,
  onChange,
  disabled,
}) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {label}
    </label>
    <CommonInput
      name={label}
      type="number"
      min="0"
      max="100"
      step="1"
      value={Number.isFinite(value) ? String(value) : ""}
      disabled={disabled}
      error={error}
      onChange={e => {
        const raw = e.target.value;
        if (raw === "") {
          onChange(Number.NaN);
          return;
        }
        onChange(Number(raw));
      }}
    />
    {!error && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const HealthScoreThresholdsOrgSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    healthScoreThresholds,
    healthScoreThresholdsLoading,
    healthScoreThresholdsUpdateLoading,
    healthScoreThresholdsUpdateError,
    healthScoreThresholdsUpdateSuccess,
  } = useAppSelector(state => state.vendorAdmin);

  const [settings, setSettings] = useState<HealthScoreThresholds>(
    healthScoreThresholds || DEFAULT_HEALTH_SCORE_THRESHOLDS
  );

  const bh = settings.business_health;
  const ah = settings.account_health;

  const validation = useMemo(
    () => validateHealthScoreThresholds(settings),
    [settings]
  );

  useEffect(() => {
    void dispatch(fetchHealthScoreThresholds());
  }, [dispatch]);

  useEffect(() => {
    setSettings(healthScoreThresholds || DEFAULT_HEALTH_SCORE_THRESHOLDS);
  }, [healthScoreThresholds]);

  useEffect(() => {
    if (healthScoreThresholdsUpdateSuccess) {
      toast.success("Health score thresholds saved");
      dispatch(clearHealthScoreThresholdsUpdateState());
    }
  }, [healthScoreThresholdsUpdateSuccess, dispatch]);

  useEffect(() => {
    if (healthScoreThresholdsUpdateError) {
      toast.error(healthScoreThresholdsUpdateError);
      dispatch(clearHealthScoreThresholdsUpdateState());
    }
  }, [healthScoreThresholdsUpdateError, dispatch]);

  const patchBh = (patch: Partial<BusinessHealthThresholds>) => {
    setSettings(prev => ({
      ...prev,
      business_health: { ...prev.business_health, ...patch },
    }));
  };

  const patchAh = (patch: Partial<AccountHealthThresholds>) => {
    setSettings(prev => ({
      ...prev,
      account_health: { ...prev.account_health, ...patch },
    }));
  };

  const handleSave = () => {
    if (!validation.valid) {
      toast.error(
        validation.messages[0] || "Fix validation errors before saving"
      );
      return;
    }
    void dispatch(
      updateHealthScoreThresholds(normalizeHealthScoreThresholds(settings))
    );
  };

  const handleReset = () => {
    setSettings(DEFAULT_HEALTH_SCORE_THRESHOLDS);
  };

  const bhLegend = formatBusinessHealthBandLegend(
    Number.isFinite(bh.concern_min) &&
      Number.isFinite(bh.stable_min) &&
      Number.isFinite(bh.strong_above)
      ? bh
      : DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const ahLegend = formatAccountHealthBandLegend(
    Number.isFinite(ah.triage_min) &&
      Number.isFinite(ah.defend_min) &&
      Number.isFinite(ah.delegate_min) &&
      Number.isFinite(ah.expand_min)
      ? ah
      : DEFAULT_HEALTH_SCORE_THRESHOLDS.account_health
  );
  const saving = healthScoreThresholdsUpdateLoading;
  const canSave = validation.valid && !saving;

  return (
    <div className="settings-section rounded-lg border border-gray-200 bg-white shadow">
      <div className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-purple-600" />
          <h2 className="fs-18 fw-600 mb-0 text-gray-900">
            Health Score Thresholds
          </h2>
        </div>
        <p className="mb-6 text-sm text-gray-600">
          Configure Business Health and Account Health score bands used on Birds
          Eye, My Accounts, and related tooltips.
        </p>

        {healthScoreThresholdsLoading ? (
          <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
            Loading…
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-lg border border-gray-200 p-5">
              <div className="mb-4 flex items-center gap-2">
                <HeartPulse className="h-4 w-4 text-purple-600" />
                <h3 className="text-base font-semibold text-gray-900">
                  Business Health
                </h3>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                From public data briefings — Concern / Stable / Strong.
              </p>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <ThresholdField
                  label="Concern min"
                  hint="Scores from this value up to Stable min − 1"
                  value={bh.concern_min}
                  error={validation.errors.business_health.concern_min}
                  onChange={v => patchBh({ concern_min: v })}
                  disabled={saving}
                />
                <ThresholdField
                  label="Stable min"
                  hint="Stable from this value up to Strong above − 1"
                  value={bh.stable_min}
                  error={validation.errors.business_health.stable_min}
                  onChange={v => patchBh({ stable_min: v })}
                  disabled={saving}
                />
                <ThresholdField
                  label="Strong above"
                  hint="Scores greater than or equal to this value are Strong"
                  value={bh.strong_above}
                  error={validation.errors.business_health.strong_above}
                  onChange={v => patchBh({ strong_above: v })}
                  disabled={saving}
                />
              </div>
              <div className="rounded-md bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
                Preview: {bhLegend.concern} · {bhLegend.stable} ·{" "}
                {bhLegend.strong}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-5">
              <div className="mb-4 flex items-center gap-2">
                <Activity className="h-4 w-4 text-purple-600" />
                <h3 className="text-base font-semibold text-gray-900">
                  Account Health
                </h3>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                From enterprise KPIs — Triage / Defend / Monitor / Expand.
              </p>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <ThresholdField
                  label="Triage min"
                  hint="Lower bound for Triage band"
                  value={ah.triage_min}
                  error={validation.errors.account_health.triage_min}
                  onChange={v => patchAh({ triage_min: v })}
                  disabled={saving}
                />
                <ThresholdField
                  label="Defend min"
                  hint="Start of Defend band"
                  value={ah.defend_min}
                  error={validation.errors.account_health.defend_min}
                  onChange={v => patchAh({ defend_min: v })}
                  disabled={saving}
                />
                <ThresholdField
                  label="Monitor min"
                  hint="Start of Monitor band"
                  value={ah.delegate_min}
                  error={validation.errors.account_health.delegate_min}
                  onChange={v => patchAh({ delegate_min: v })}
                  disabled={saving}
                />
                <ThresholdField
                  label="Expand min"
                  hint="Scores at or above this are Expand"
                  value={ah.expand_min}
                  error={validation.errors.account_health.expand_min}
                  onChange={v => patchAh({ expand_min: v })}
                  disabled={saving}
                />
              </div>
              <div className="rounded-md bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
                Preview: {ahLegend.triage} · {ahLegend.defend} ·{" "}
                {ahLegend.delegate} · {ahLegend.expand}
              </div>
            </div>

            {!validation.valid && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {validation.messages.map(msg => (
                  <div key={msg}>{msg}</div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={handleReset}
                disabled={saving}
              >
                Reset to defaults
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!canSave}
                loading={saving}
                icon={<Save className="h-4 w-4" />}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthScoreThresholdsOrgSettings;
